import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PilotPayHttpClient } from '../../core';
import {
	ICalculatedPayScaleRates,
	IMinimumEffectiveDateResponse,
	IPayScale,
	IPayScaleGlobalSettings,
	IPayScaleGlobalSettingsHistory,
	IPayScaleHistory,
	IPayScalesService,
	IPilotPayScale
} from './pay-scales.types';

/**
 * PayScalesService
 */
@Injectable()
export class PayScalesService implements IPayScalesService {
	constructor(http: PilotPayHttpClient) {
		this.http = http;
	}

	private http: PilotPayHttpClient;

	public calculatePayScaleRates(
		numberOfTrips: number,
		payScale: IPayScale | IPilotPayScale
	): ICalculatedPayScaleRates[] {
		const { perTripPay, isApRatingPayScale, constantApBonus } = payScale;

		// Pilots have a separate YoS data structure; they have a single YoS, so it's flattened instead of nested.
		let yearOfServiceRecords: any;
		if ((payScale as IPayScale).yearOfServiceRecords) {
			yearOfServiceRecords = (payScale as IPayScale).yearOfServiceRecords;
		} else if ((payScale as IPilotPayScale).yearsOfService) {
			const {
				yearsOfService,
				guaranteedHours,
				hourlyRateDollars,
				instructorPayDollars
			} = payScale as IPilotPayScale;
			yearOfServiceRecords = [{ yearsOfService, guaranteedHours, hourlyRateDollars, instructorPayDollars }];
		}

		return yearOfServiceRecords.map(
			({ yearsOfService, guaranteedHours, hourlyRateDollars, instructorPayDollars }) => {
				const biWeeklyRate: number = (guaranteedHours * hourlyRateDollars * 12) / 26;
				const biWeeklyInstructorRate: number = biWeeklyRate + instructorPayDollars;
				const monthlyRate: number = guaranteedHours * hourlyRateDollars;
				const annualRate: number = monthlyRate * 12;
				const tripRigRate: number = numberOfTrips * perTripPay + annualRate;

				const calculatedRow: ICalculatedPayScaleRates = {
					yearsOfService,
					guaranteedHours,
					hourlyRateDollars,
					instructorPayDollars,
					biWeeklyRate,
					biWeeklyInstructorRate,
					monthlyRate,
					annualRate,
					tripRigRate
				};

				if (isApRatingPayScale) {
					const apBiWeeklyRate: number = biWeeklyRate * (1 + constantApBonus / 100);
					const apAnnualRate: number = annualRate * (1 + constantApBonus / 100);
					const apTripRigRate: number = numberOfTrips * perTripPay + apAnnualRate;

					calculatedRow.apBiWeeklyRate = apBiWeeklyRate;
					calculatedRow.apAnnualRate = apAnnualRate;
					calculatedRow.apTripRigRate = apTripRigRate;
				}

				return calculatedRow;
			}
		);
	}

	public getPayScales(includeFuture: boolean = false): Observable<IPayScale[]> {
		let url: string = '/api/pay-scales';

		if (includeFuture) {
			url += `?includeFuture=${includeFuture}`;
		}

		return this.http.getRequest(url);
	}

	public getAircraftModel(): Observable<Object> {
		return this.http.getRequest('/api/aircraft-models');
	}

	public getJobTitles(): Observable<Object> {
		return this.http.getRequest('/api/job-titles');
	}

	public getPayScale(payScaleId: number): Observable<IPayScale[]> {
		return this.http.getRequest(`/api/pay-scales/${payScaleId}`);
	}

	public createPayScale(payload: IPayScale): Observable<IPayScale> {
		return this.http.postRequest('/api/pay-scales', payload);
	}

	public updatePayScale(payload: IPayScale): Observable<IPayScale> {
		return this.http.putRequest(`/api/pay-scales/${payload.payScaleId}`, payload);
	}

	public getMinimumEffectiveDate(payScaleId: number): Observable<IMinimumEffectiveDateResponse> {
		return this.http.getRequest(`/api/pay-scales/${payScaleId}/minimum-effective-date`);
	}

	public getHistory(payScaleId: number): Observable<IPayScaleHistory[]> {
		return this.http.getRequest(`/api/pay-scales/${payScaleId}/history`);
	}

	public getScheduledChanges(payScaleId: number): Observable<IPayScaleHistory[]> {
		return this.http.getRequest(`/api/pay-scales/${payScaleId}/scheduled-changes`);
	}

	public deleteScheduledChanges(payScaleId: number): Observable<Object> {
		return this.http.deleteRequest(`/api/pay-scales/${payScaleId}/scheduled-changes`);
	}

	public getPayScaleSetting(): Observable<IPayScaleGlobalSettings> {
		return this.http.getRequest('/api/pay-scales/global-settings');
	}

	public createPayScaleSetting(payload: IPayScaleGlobalSettings): Observable<Object> {
		return this.http.postRequest('/api/pay-scales/global-settings', payload);
	}

	public getPayScaleSettingsHistory(): Observable<IPayScaleGlobalSettingsHistory[]> {
		return this.http.getRequest('/api/pay-scales/global-settings/history');
	}

	public getPayScaleGlobalSettingChanges(): Observable<IPayScaleGlobalSettingsHistory[]> {
		return this.http.getRequest('/api/pay-scales/global-settings/scheduled-changes');
	}

	public deletePayScaleGlobalSettingScheduledChanges(): Observable<IPayScaleGlobalSettingsHistory[]> {
		return this.http.deleteRequest('/api/pay-scales/global-settings/scheduled-changes');
	}
}
